/**
 * PageLayout — common chrome wrapper for every owner-hub page.
 *
 *   Title (h1) + optional subtitle on the left
 *   Optional right-aligned actions slot (button row)
 *   Content area with consistent padding + max-width
 */

import type { ReactNode } from 'react'

interface PageLayoutProps {
  title: string
  subtitle?: string
  actions?: ReactNode
  children: ReactNode
  /** Disable the inner max-width for full-bleed pages (e.g. Smart Import). */
  fullBleed?: boolean
}

export function PageLayout({
  title,
  subtitle,
  actions,
  children,
  fullBleed = false,
}: PageLayoutProps) {
  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <header className="border-b border-slate-200 bg-white/60 px-6 py-4 dark:border-slate-700 dark:bg-slate-900/40">
        <div className="mx-auto flex w-full max-w-6xl flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              {title}
            </h1>
            {subtitle && (
              <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                {subtitle}
              </p>
            )}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      </header>
      <main
        className={`flex-1 px-6 py-6 ${
          fullBleed ? '' : 'mx-auto w-full max-w-6xl'
        }`}
      >
        {children}
      </main>
    </div>
  )
}
